import { Order } from '../types';
import { DistanceService } from './parserService';
import { geocodeAddress, enrichOrderWithLocation } from './reconService';
import { isValidGeoCoordinate } from './math/geo';
import {
    extractIfoodCustomerProxyPhoneFromOrderObservation,
    extractIfoodSupportIdFromOrderObservation,
} from './ifoodConfirmationParser';

/**
 * v41.16: Mapper Cardápio Web - Full Integrity
 * - Mapeamento explícito de status de pagamento
 * - Cálculo correto de SLA para pedidos agendados
 * - Suporte a ID humano e iFood
 */
export const mapCardapioToInternal = async (
    posOrder: any,
    hubConfig: { lat: number, lng: number, googleMapsApiKey?: string, hereMapsApiKey?: string, storeId: string, storeCity?: string },
    existingCoords?: { lat?: number, lng?: number }
): Promise<Order> => {

    const ifoodSupportId = extractIfoodSupportIdFromOrderObservation(posOrder);
    const customerProxyPhone = extractIfoodCustomerProxyPhoneFromOrderObservation(posOrder);

    let internalStatus: Order['status'] = 'pending';
    let isReady = false;

    // Status Mapping based on API Docs
    switch (posOrder.status) {
        case 'waiting_confirmation':
            // Explicitly mark as needing confirmation, mapped to internal 'waiting_confirmation' if supported,
            // or 'pending' but with a flag. Let's use 'waiting_confirmation' to be explicit if the type allows,
            // otherwise 'pending'. The prompt requested "status pending que for" -> show Accept button.
            internalStatus = 'waiting_confirmation';
            break;
        case 'pending_payment':
        case 'pending_online_payment':
            internalStatus = 'payment_pending';
            break;
        case 'confirmed':
            internalStatus = 'confirmed';
            break;
        case 'scheduled_confirmed':
            internalStatus = 'scheduled_confirmed';
            break;
        case 'ready':
            internalStatus = 'ready';
            isReady = true;
            break;
        case 'waiting_to_catch': // Pronto para retirada
            internalStatus = 'ready';
            isReady = true;
            break;
        case 'released': // Saiu para entrega
            internalStatus = 'dispatched';
            isReady = true;
            break;
        case 'delivered':
        case 'closed':
            internalStatus = 'delivered';
            isReady = true;
            break;
        case 'canceled':
        case 'canceling': // Em processo de cancelamento
            internalStatus = 'canceled';
            break;
        default:
            internalStatus = 'confirmed';
    }

    const isDelivery = posOrder.order_type === 'delivery';
    const addr = posOrder.delivery_address;
    const fullAddress = !isDelivery ? 'Retirada em Balcão' : `${addr?.street || ''}, ${addr?.number || 'S/N'} - ${addr?.neighborhood || ''}`;

    // v46.6: Extração explícita do número
    const addressNumber = addr?.number || (addr?.street?.match(/,?\s*(\d+)$/)?.[1]) || 'S/N';

    // v2025.02.14: Tentar múltiplas fontes de coordenadas (API pode retornar em diferentes estruturas)
    // 1. existingCoords (do pedido já existente)
    // 2. addr.latitude/longitude (estrutura comum)
    // 3. addr.coordinates.lat/lng (estrutura alternativa)
    // 4. posOrder.delivery_lat/delivery_lng (estrutura plana)
    const rawLat = existingCoords?.lat 
        || addr?.latitude 
        || addr?.coordinates?.lat 
        || addr?.coordinates?.latitude 
        || posOrder.delivery_lat 
        || posOrder.latitude;
    const rawLng = existingCoords?.lng 
        || addr?.longitude 
        || addr?.coordinates?.lng 
        || addr?.coordinates?.longitude 
        || posOrder.delivery_lng 
        || posOrder.longitude;
    
    let lat = parseFloat(String(rawLat || 'NaN'));
    let lng = parseFloat(String(rawLng || 'NaN'));
    let distance = 0;
    let estimatedDuration = 0;

    // v2024.2: Tentar enrichment completo com HERE/Google (inclui duration estimada)
    const needsEnrichment = !isValidGeoCoordinate(lat, lng) && isDelivery && (hubConfig.googleMapsApiKey || hubConfig.hereMapsApiKey);
    
    if (needsEnrichment) {
        try {
            // Cria uma ordem temporária para usar o enrichment completo
            const tempOrder: Partial<Order> = {
                address: fullAddress,
                lat: undefined,
                lng: undefined,
                distance: 0,
                estimatedDuration: 0
            };
            
            const enriched = await enrichOrderWithLocation(tempOrder as Order, {
                lat: hubConfig.lat,
                lng: hubConfig.lng,
                googleMapsApiKey: hubConfig.googleMapsApiKey,
                hereMapsApiKey: hubConfig.hereMapsApiKey,
                storeId: hubConfig.storeId
            });
            
            if (enriched && isValidGeoCoordinate(enriched.lat, enriched.lng)) {
                lat = enriched.lat;
                lng = enriched.lng;
                distance = enriched.distance || 0;
                estimatedDuration = enriched.estimatedDuration || 0;
            } else {
                lat = undefined;
                lng = undefined;
            }
        } catch (enrichError) {
            console.warn(`[CardapioMapper] Enrichment failed for order ${posOrder.id}:`, enrichError);
            lat = undefined;
            lng = undefined;
        }
    } else if (isValidGeoCoordinate(lat, lng) && (hubConfig.googleMapsApiKey || hubConfig.hereMapsApiKey)) {
        // Já temos coordenadas válidas, buscar duration estimada
        try {
            const tempOrder: Partial<Order> = {
                address: fullAddress,
                lat: lat,
                lng: lng,
                distance: DistanceService.getDistanceSync(hubConfig.lat, hubConfig.lng, lat, lng),
                estimatedDuration: 0
            };
            
            const enriched = await enrichOrderWithLocation(tempOrder as Order, {
                lat: hubConfig.lat,
                lng: hubConfig.lng,
                googleMapsApiKey: hubConfig.googleMapsApiKey,
                hereMapsApiKey: hubConfig.hereMapsApiKey,
                storeId: hubConfig.storeId
            });
            
            if (enriched) {
                distance = enriched.distance || DistanceService.getDistanceSync(hubConfig.lat, hubConfig.lng, lat, lng);
                estimatedDuration = enriched.estimatedDuration || 0;
            }
        } catch (enrichError) {
            console.warn(`[CardapioMapper] Duration enrichment failed for order ${posOrder.id}:`, enrichError);
            distance = DistanceService.getDistanceSync(hubConfig.lat, hubConfig.lng, lat, lng);
        }
    } else if (isValidGeoCoordinate(lat, lng)) {
        // Sem API keys, calcula distância básica
        distance = DistanceService.getDistanceSync(hubConfig.lat, hubConfig.lng, lat, lng);
    } else {
        // Coordenadas inválidas e sem API keys
        lat = undefined;
        lng = undefined;
    }

    const createdAt = posOrder.created_at ? new Date(posOrder.created_at).getTime() : Date.now();
    const orderTiming: Order['orderTiming'] = posOrder.order_timing === 'scheduled' ? 'scheduled' : 'immediate';
    const scheduledStartAt = posOrder.schedule?.scheduled_date_time_start
        ? new Date(posOrder.schedule.scheduled_date_time_start).getTime()
        : undefined;
    const scheduledEndAt = posOrder.schedule?.scheduled_date_time_end
        ? new Date(posOrder.schedule.scheduled_date_time_end).getTime()
        : undefined;

    // Cálculo de SLA (TimeLimit)
    let timeLimit = posOrder.estimated_time || 45;

    // Se for agendado, calcula SLA baseado na janela de entrega
    if (scheduledEndAt) {
        const now = Date.now();
        const elapsedFromCreation = Math.max(0, (now - createdAt) / 60000);
        const remainingUntilWindowEnd = (scheduledEndAt - now) / 60000;
        const scheduledDeadline = Math.round(remainingUntilWindowEnd + elapsedFromCreation);
        timeLimit = Math.max(1, scheduledDeadline);
    }

    return {
        id: String(posOrder.id),
        platformId: String(posOrder.id),
        displayId: posOrder.display_id?.toString() || '', // ID da Loja (ex: #48)
        shortCode: posOrder.external_display_id?.toString() || '', // iFood (ex: #1234)
        customerName: posOrder.customer?.name || 'Cliente',
        phone: posOrder.customer?.phone || '',
        address: fullAddress,
        streetAddress: addr?.street || 'Balcão',
        addressNumber: isDelivery ? addressNumber : undefined,
        neighborhood: addr?.neighborhood || 'Loja',
        complement: addr?.complement || '', // v46.2: Mapeamento de complemento
        reference: addr?.reference || '',   // v46.2: Mapeamento de referência
        items: posOrder.items?.map((i: any) => `${i.quantity}x ${i.name}`).join(', ').substring(0, 500),
        orderValue: parseFloat(posOrder.total || 0),
        deliveryFee: parseFloat(posOrder.delivery_fee || 0),
        paymentMethod: posOrder.payments?.[0]?.payment_method || 'A Receber',
        timeLimit: timeLimit,
        createdAt,
        salesChannel: posOrder.sales_channel || 'CardapioWeb',
        type: isDelivery ? 'DELIVERY' : 'TAKEOUT',
        orderTiming,
        scheduledStartAt,
        scheduledEndAt,
        ifoodSupportId,
        customerProxyPhone,
        status: internalStatus,
        isReady,
        lat: isValidGeoCoordinate(lat, lng) ? lat : undefined,
        lng: isValidGeoCoordinate(lat, lng) ? lng : undefined,
        distance,
        estimatedDuration,
        isLoading: false,
        source: 'CARDAPIO_WEB',
        costThirdParty: 0,
        statusUpdatedAt: posOrder.updated_at ? new Date(posOrder.updated_at).getTime() : undefined
    };
};
